import { useEffect } from 'react';
import type { C3Result } from './hooks';

export interface Combined {
  /** Every resource has records to show. They can be the last good read if a later one failed. */
  ready: boolean;
  /** Something has not been read yet, or is read again after a failure: show the skeleton. */
  loading: boolean;
  /** A read failed and none is under way: say it could not load. */
  error: boolean;
  /** At least one resource is being read, the first time or on a tick. */
  fetching: boolean;
  /** Any resource's last read failed, including while it is read again. */
  failed: boolean;
  /** The moment the newest resource stands for, in ms. */
  updatedAt: number;
}

/**
 * Folds the state of the resources one screen is made of into the three it shows: loading, error,
 * and ready. A screen that needs all of them treats one failure as a failure of the whole, as the
 * `Promise.all` it replaces did.
 */
export function combine(results: C3Result<unknown>[]): Combined {
  const failed = results.some((r) => r.error != null);
  const fetching = results.some((r) => r.fetching);
  const waiting = results.some((r) => r.data === undefined && r.error == null);
  return {
    ready: results.every((r) => r.data !== undefined),
    loading: waiting || (failed && fetching),
    error: failed && !fetching,
    fetching,
    failed,
    updatedAt: results.reduce((latest, r) => Math.max(latest, r.updatedAt), 0),
  };
}

/** The raw error goes to the console; the screen says it in plain words. */
export function useLogFailure(label: string, results: C3Result<unknown>[]): void {
  const failure = results.find((r) => r.error)?.error;
  useEffect(() => {
    if (failure) console.error(label, failure);
  }, [label, failure]);
}
